import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncList, useLastKnownTotal, useTableState } from "@/ds";
import {
  DEFAULT_PERMISSION_SORT,
  isPermissionSortField,
  listPermissions,
} from "@/modules/permissions";
import type { Permission, PermissionSort, PermissionSortField } from "@/modules/permissions";
import { PERMISSIONS_ERROR_MESSAGE, PERMISSIONS_FORBIDDEN_MESSAGE } from "../permissions.constants";

interface ListaParams {
  busca: string;
  page: number;
  pageSize: number;
  sort: PermissionSort;
}

/** Model da listagem: busca, página e ordenação no servidor; mudar a busca ou a ordem volta à página 1. */
export function usePermissionsLista() {
  const [search, setSearch] = useState("");
  const busca = search.trim();

  const { lastTotal, remember } = useLastKnownTotal();
  const tabela = useTableState<PermissionSortField>({
    isSortField: isPermissionSortField,
    total: lastTotal,
    resetKey: busca,
  });
  // Sem coluna escolhida a consulta segue a ordem padrão (Nome A–Z) e nenhum cabeçalho mostra a seta.
  const appliedSort: PermissionSort = tabela.sort ?? DEFAULT_PERMISSION_SORT;

  const lista = useAsyncList<ListaParams, Permission>({
    fetcher: async (params, signal) => {
      const { permissions, total } = await listPermissions({
        pesquisa: params.busca,
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        signal,
      });
      return { items: permissions, total };
    },
    params: { busca, page: tabela.page + 1, pageSize: tabela.rows, sort: appliedSort },
    debounceKey: busca,
    forbiddenMessage: PERMISSIONS_FORBIDDEN_MESSAGE,
    errorMessage: PERMISSIONS_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
  remember(lista.status, lista.total);

  return {
    search,
    busca,
    appliedSort,
    hasSearch: busca !== "",
    items: lista.items,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    tabela,
    /** Sem espaço no começo (a busca não nasce de um espaço) e sem refazer o estado quando nada mudou. */
    buscar: (texto: string) => {
      const proximo = texto.trimStart();
      if (proximo !== search) setSearch(proximo);
    },
    clearSearch: () => setSearch(""),
  };
}

export type UsePermissionsListaResult = ReturnType<typeof usePermissionsLista>;
